import { useMemo, useState } from 'react'
import { sum } from 'lodash'
import Styles from './AddMoreContentModal.module.scss'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { ChevronRight } from 'almanac-ui/icons'
import { ContentType } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { ChevronDown2, TikTokBrand, TwitterBrand, YouTubeBrand } from 'blaze-ui/icons'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { FacebookBrand, MailchimpBrand, WordPressBrand, WixBrand, ShopifyBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { AddMoreContentProps } from './Types'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { GoHighLevelBrand } from 'blaze-ui/icons/35/GoHighLevelBrand'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { BrandPlanTactic } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import CrossPostIcon from '../../../../../../components/FirstTimeExperienceModal/steps/BrandPlanAwareness/CrossPostIcon'
import { useModals } from 'almanac-ui/Modal'
import { useAutopilotLimits } from 'hooks/useAutopilotLimits'
import { calculateChannelCounts, getCountFromDirectProperties } from './utils'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { ChannelRow, Channel } from './ChannelRow'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'

const MAX_ADDITIONALS = 50
const MAX_TOTAL = 200

export const AddMoreContent = ({
  onNext,
  targetSchedule,
  additionals,
  setAdditionals,
  excludedAccounts,
  setExcludedAccounts,
  onBack,
  backButtonLabel = 'Back',
  needsCredits,
  creditsNeeded,
}: AddMoreContentProps) => {
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { closeModal } = useModals()
  const isYoutubeEnabled = useFlipperFlag('youtube_integration')
  const { maxChannels, disabledChannels, maxPostsPerChannel } = useAutopilotLimits()
  const workspace = useCurrentWorkspace()
  const [isOtherExpanded, setIsOtherExpanded] = useState(false)
  const { data: externalIntegrationsData } = useExternalIntegrations()

  const channels = useMemo(() => {
    // Determine blog post label and icon based on connected integration

    const getBlogPlatformDisplay = (): { name: string; icon: typeof WordPressBrand } => {
      switch (connectedBlogIntegration?.provider) {
        case ExternalIntegrationEnum.GoHighLevel:
          return { name: 'GoHighLevel Blog Posts', icon: GoHighLevelBrand }
        case ExternalIntegrationEnum.Wix:
          return { name: 'Wix Blog Posts', icon: WixBrand }
        case ExternalIntegrationEnum.Shopify:
          return { name: 'Shopify Blog Posts', icon: ShopifyBrand }
        case ExternalIntegrationEnum.WordPress:
        default:
          return { name: 'WordPress Blog Posts', icon: WordPressBrand }
      }
    }

    const { name: blogPostName, icon: blogPostIcon } = getBlogPlatformDisplay()

    // Get enabled tactics from targetSchedule
    const enabledTactics = targetSchedule?.tactics || []

    // Create crosspost providers array based on enabled tactics
    const crosspostProviders = [
      enabledTactics.includes(BrandPlanTactic.Instagram) && ExternalIntegrationEnum.Instagram,
      enabledTactics.includes(BrandPlanTactic.Facebook) && ExternalIntegrationEnum.Facebook,
      enabledTactics.includes(BrandPlanTactic.Twitter) && ExternalIntegrationEnum.X,
      enabledTactics.includes(BrandPlanTactic.LinkedIn) && ExternalIntegrationEnum.LinkedIn,
    ].filter(Boolean) as ExternalIntegrationEnum[]

    // Create video crosspost providers array based on enabled tactics
    const videoCrosspostProviders = [
      enabledTactics.includes(BrandPlanTactic.Instagram) && ExternalIntegrationEnum.Instagram,
      enabledTactics.includes(BrandPlanTactic.YouTube) && ExternalIntegrationEnum.YouTube,
      enabledTactics.includes(BrandPlanTactic.TikTok) && ExternalIntegrationEnum.TikTok,
    ].filter(Boolean) as ExternalIntegrationEnum[]

    return [
      ...(crosspostProviders.length > 0
        ? [
            {
              name: 'Cross Posts',
              type: 'crosspost_post',
              icon: CrossPostIcon,
              isCrosspost: true,
              providers: crosspostProviders,
            },
          ]
        : []),
      ...(videoCrosspostProviders.length > 0
        ? [
            {
              name: 'Video Cross Posts',
              type: 'video_crosspost_post',
              icon: CrossPostIcon,
              isCrosspost: true,
              providers: videoCrosspostProviders,
            },
          ]
        : []),
      { name: 'Instagram Posts', type: 'instagram_post', icon: InstagramBrand },
      { name: 'Instagram Stories', type: 'instagram_story', icon: InstagramBrand },
      { name: 'Instagram Reels', type: 'instagram_reel', icon: InstagramBrand },
      ...(isYoutubeEnabled ? [{ name: 'YouTube Shorts', type: 'youtube_short', icon: YouTubeBrand }] : []),
      { name: 'TikTok Posts', type: 'tiktok_post', icon: TikTokBrand },
      { name: 'Facebook Posts', type: 'facebook_post', icon: FacebookBrand },
      { name: 'LinkedIn Posts', type: 'linkedin_post', icon: LinkedInBrand },
      { name: 'X/Twitter Posts', type: 'twitter_post', icon: TwitterBrand },
      { name: blogPostName, type: 'blog_post', icon: blogPostIcon },
      { name: 'Emails', type: 'newsletter', icon: MailchimpBrand },
    ] as Channel[]
  }, [connectedBlogIntegration?.provider, isYoutubeEnabled, targetSchedule?.tactics])

  const channelCounts = useMemo(() => {
    return calculateChannelCounts(targetSchedule)
  }, [targetSchedule])

  // Split channels into scheduled (planned or generated content > 0) and other channels
  const { scheduledChannels, otherChannels } = useMemo(() => {
    const scheduled: Channel[] = []
    const other: Channel[] = []

    channels.forEach((channel) => {
      const plannedCount = getCountFromDirectProperties(channel.type, targetSchedule)
      if (plannedCount > 0) {
        scheduled.push(channel)
      } else {
        other.push(channel)
      }
    })

    return { scheduledChannels: scheduled, otherChannels: other }
  }, [channels, targetSchedule])

  const usedChannels = useMemo(() => {
    return channels
      .filter((channel) => {
        // First check channelCounts (from contents array), if not available, check direct properties
        const currentCount =
          channelCounts?.[channel.type] !== undefined
            ? channelCounts[channel.type]
            : getCountFromDirectProperties(channel.type, targetSchedule)
        const additionalCount = additionals[channel.type] || 0
        return currentCount + additionalCount > 0
      })
      .map((channel) => channel.type)
  }, [channels, channelCounts, additionals, targetSchedule])

  const channelsCount = useMemo(() => {
    return sum(Object.values(channelCounts || {}))
  }, [channelCounts])

  const additionalsCount = useMemo(() => {
    return sum(Object.values(additionals))
  }, [additionals])

  const totalCount = useMemo(() => {
    return additionalsCount + channelsCount
  }, [additionalsCount, channelsCount])

  const maxTotal = Math.min(MAX_TOTAL, maxPostsPerChannel)
  const showLimits = maxTotal === maxPostsPerChannel

  const exceedsMax = additionalsCount >= MAX_ADDITIONALS || totalCount >= MAX_TOTAL

  const handleRemoveAdditional = (type: ContentType) => {
    setAdditionals((prev) => ({ ...prev, [type]: Math.max(prev[type] - 1, 0) }))
  }

  const handleAddAdditional = (type: ContentType) => {
    setAdditionals((prev) => ({ ...prev, [type]: prev[type] + 1 }))
  }

  const handleToggleAccount = (accountId: number, checked: boolean) => {
    setExcludedAccounts((prev) => ({ ...prev, [accountId]: !checked }))
  }

  return (
    <>
      <Modal.Header onBack={onBack} title="Create Content" />
      <Modal.Content>
        <div className={Styles.contentContainer}>
          <Heading level={5}>Add additional content from your content plan</Heading>
          {workspace?.is_ap_freemium && (
            <Text variant="secondary">{`On your Free plan, you can auto-generate up to ${maxPostsPerChannel} posts across ${maxChannels} channels (up to ${
              maxPostsPerChannel * maxChannels
            } posts total).`}</Text>
          )}
          {/* Scheduled channels section */}
          {scheduledChannels.length > 0 && (
            <div className={Styles.channels}>
              <div className={Styles.header}>
                <Text variant="label">Type</Text>
                <div className={Styles.addHeader}>
                  <Text variant="label">Scheduled</Text>
                  <Text variant="label">Add</Text>
                </div>
              </div>
              <div className={Styles.body}>
                {scheduledChannels.map((channel, index) => (
                  <ChannelRow
                    key={`${channel.type}-${index}`}
                    channel={channel}
                    index={index}
                    channelCounts={channelCounts}
                    additionals={additionals}
                    disabledChannels={disabledChannels}
                    maxChannels={maxChannels}
                    usedChannels={usedChannels}
                    showLimits={showLimits}
                    maxPostsPerChannel={maxPostsPerChannel}
                    targetSchedule={targetSchedule}
                    exceedsMax={exceedsMax}
                    showScheduledCount
                    externalIntegrations={externalIntegrationsData?.external_integrations}
                    excludedAccounts={excludedAccounts}
                    onToggleAccount={handleToggleAccount}
                    onAdd={() => handleAddAdditional(channel.type)}
                    onRemove={() => handleRemoveAdditional(channel.type)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Other channels collapsible section */}
          {otherChannels.length > 0 && (
            <div className={Styles.otherSection}>
              <div className={Styles.body}>
                <div
                  className={Styles.collapsibleHeader}
                  onClick={() => setIsOtherExpanded(!isOtherExpanded)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => ['Enter', ' '].includes(e.key) && setIsOtherExpanded(!isOtherExpanded)}
                >
                  <Heading level={5}>Other content available to generate</Heading>
                  {isOtherExpanded ? <ChevronDown2 /> : <ChevronRight size={12} />}
                </div>
                {isOtherExpanded &&
                  otherChannels.map((channel, index) => (
                    <ChannelRow
                      key={`${channel.type}-${index}`}
                      channel={channel}
                      index={index}
                      channelCounts={channelCounts}
                      additionals={additionals}
                      disabledChannels={disabledChannels}
                      maxChannels={maxChannels}
                      usedChannels={usedChannels}
                      showLimits={showLimits}
                      maxPostsPerChannel={maxPostsPerChannel}
                      targetSchedule={targetSchedule}
                      exceedsMax={exceedsMax}
                      externalIntegrations={externalIntegrationsData?.external_integrations}
                      excludedAccounts={excludedAccounts}
                      onToggleAccount={handleToggleAccount}
                      onAdd={() => handleAddAdditional(channel.type)}
                      onRemove={() => handleRemoveAdditional(channel.type)}
                    />
                  ))}
              </div>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={onBack || closeModal}>
            {onBack ? backButtonLabel : 'Cancel'}
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            onPress={onNext}
            size="lg"
            isDisabled={sum(Object.values(additionals)) === 0 && !needsCredits}
          >
            <div className={Styles.buttonWithCredits}>
              <span>Add Topic & Media </span>
              {creditsNeeded > 0 && (
                <>
                  <Credits color="var(--light-100)" />
                  <span>{creditsNeeded}</span>
                </>
              )}
            </div>
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
